/**
 * Operational Playbooks — Structured Guidance Layer (I-A-13)
 *
 * Maps recommendation types to structured, multi-step review flows.
 * All playbooks are predefined, deterministic, and advisory.
 * Zero backend mutations, zero workflow enforcement.
 */

export interface PlaybookStep {
  step_id: string;
  title: string;
  description: string;
  context_hint: string;
  route_type: 'shop' | 'intelligence' | 'none';
}

export interface Playbook {
  playbook_type: string;
  title: string;
  summary: string;
  steps: PlaybookStep[];
}

type RecommendationType =
  | 'OVERLOAD_ATTENTION'
  | 'UNDERUTILIZATION'
  | 'COMPLETION_WEAKNESS'
  | 'WAITER_PRESSURE'
  | 'CAPACITY_IMBALANCE';

// ── Playbook Templates ───────────────────────────────

const OVERLOAD_PLAYBOOK: Playbook = {
  playbook_type: 'OVERLOAD',
  title: 'Overload Review Playbook',
  summary:
    'This playbook is suggested because the shop appears overloaded. Follow these steps to review workload and identify pressure points.',
  steps: [
    {
      step_id: 'overload-1',
      title: 'Review active ticket volume and current OBLI',
      description:
        'Consider checking the current number of active tickets and the OBLI score to understand overall load intensity.',
      context_hint: 'Look for OBLI above 85 or active ticket counts significantly above normal.',
      route_type: 'shop',
    },
    {
      step_id: 'overload-2',
      title: 'Check waiter mix and same-day intake pressure',
      description:
        'Consider reviewing the ratio of waiter jobs to total active work and how much same-day intake the shop is absorbing.',
      context_hint: 'A waiter ratio above 40% may indicate front-counter scheduling pressure.',
      route_type: 'shop',
    },
    {
      step_id: 'overload-3',
      title: 'Inspect technician utilization and stalled work',
      description:
        'Consider reviewing whether technicians are carrying disproportionate load or if any jobs appear stalled.',
      context_hint: 'Look for technicians with significantly more booked time than peers.',
      route_type: 'shop',
    },
    {
      step_id: 'overload-4',
      title: 'Open shop context and review current schedule',
      description:
        'Consider opening the shop to inspect the current board and identify any immediate bottlenecks.',
      context_hint: 'The shop board shows real-time work distribution across technicians and bays.',
      route_type: 'shop',
    },
  ],
};

const WAITER_PRESSURE_PLAYBOOK: Playbook = {
  playbook_type: 'WAITER_PRESSURE',
  title: 'Waiter Pressure Review Playbook',
  summary:
    'This playbook is suggested because waiter pressure appears elevated. Follow these steps to review waiter mix and scheduling discipline.',
  steps: [
    {
      step_id: 'waiter-1',
      title: 'Review current waiter count and ratio',
      description:
        'Consider checking how many current tickets are waiters and what proportion of total work they represent.',
      context_hint: 'A high waiter ratio can crowd out longer drop-off work and create throughput issues.',
      route_type: 'shop',
    },
    {
      step_id: 'waiter-2',
      title: 'Inspect same-day intake and advisor scheduling behavior',
      description:
        'Consider reviewing how many tickets were created same-day and whether advisors are managing waiter intake appropriately.',
      context_hint: 'Look for patterns of excessive same-day waiter acceptance during peak hours.',
      route_type: 'shop',
    },
    {
      step_id: 'waiter-3',
      title: 'Check whether waiter work is crowding longer jobs',
      description:
        'Consider reviewing whether waiter jobs are displacing scheduled drop-off work or creating delays.',
      context_hint: 'Look for drop-off tickets that have been waiting longer than expected.',
      route_type: 'shop',
    },
    {
      step_id: 'waiter-4',
      title: 'Open shop context and review current board',
      description:
        'Consider opening the shop to see the current distribution of waiter vs drop-off work on the board.',
      context_hint: 'The board view shows waiter and drop-off tickets side by side.',
      route_type: 'shop',
    },
  ],
};

const COMPLETION_WEAKNESS_PLAYBOOK: Playbook = {
  playbook_type: 'COMPLETION_WEAKNESS',
  title: 'Completion Weakness Review Playbook',
  summary:
    'This playbook is suggested because completion output appears lower than the prior period. Follow these steps to identify potential bottlenecks.',
  steps: [
    {
      step_id: 'completion-1',
      title: 'Compare completion volume against prior period',
      description:
        'Consider reviewing how current completion counts compare to the previous period to understand the magnitude of the decline.',
      context_hint: 'A drop of 25% or more may indicate systemic issues rather than normal variation.',
      route_type: 'none',
    },
    {
      step_id: 'completion-2',
      title: 'Review long-running open work',
      description:
        'Consider inspecting tickets that have been in progress longer than expected to identify stalled or blocked work.',
      context_hint: 'Look for jobs that have been active for significantly longer than their estimated duration.',
      route_type: 'shop',
    },
    {
      step_id: 'completion-3',
      title: 'Inspect bottlenecks or paused/stalled tickets',
      description:
        'Consider checking for tickets in paused or diagnostic-hold states that may be contributing to lower throughput.',
      context_hint: 'Parts delays, authorization waits, and diagnostic holds can all reduce completion rates.',
      route_type: 'shop',
    },
    {
      step_id: 'completion-4',
      title: 'Open shop context and inspect active tickets',
      description:
        'Consider opening the shop to review the current ticket landscape and identify any immediate completion blockers.',
      context_hint: 'The shop board provides a real-time view of all active and in-progress work.',
      route_type: 'shop',
    },
  ],
};

const CAPACITY_IMBALANCE_PLAYBOOK: Playbook = {
  playbook_type: 'CAPACITY_IMBALANCE',
  title: 'Capacity Imbalance Review Playbook',
  summary:
    'This playbook is suggested because capacity appears unevenly distributed across your scope. Follow these steps to compare shops and identify imbalances.',
  steps: [
    {
      step_id: 'imbalance-1',
      title: 'Compare overloaded vs underutilized shops in scope',
      description:
        'Consider reviewing which shops are running hot and which have spare capacity to understand the distribution.',
      context_hint: 'The Intelligence page shows side-by-side metrics across all shops.',
      route_type: 'intelligence',
    },
    {
      step_id: 'imbalance-2',
      title: 'Review differences in load, waiter pressure, and utilization',
      description:
        'Consider comparing OBLI, waiter ratios, and utilization percentages across the affected shops.',
      context_hint: 'Look for shops with significantly different load profiles despite similar staffing.',
      route_type: 'intelligence',
    },
    {
      step_id: 'imbalance-3',
      title: 'Inspect whether one location is carrying disproportionate same-day pressure',
      description:
        'Consider checking if one shop is absorbing more same-day work than others, creating an artificial imbalance.',
      context_hint: 'Same-day intake differences can explain capacity imbalances between similar shops.',
      route_type: 'none',
    },
    {
      step_id: 'imbalance-4',
      title: 'Open Intelligence view for side-by-side context',
      description:
        'Consider opening the Intelligence page to see all shops in your scope compared directly.',
      context_hint: 'The Intelligence view provides the broadest comparative perspective.',
      route_type: 'intelligence',
    },
  ],
};

const UNDERUTILIZATION_PLAYBOOK: Playbook = {
  playbook_type: 'UNDERUTILIZATION',
  title: 'Underutilization Review Playbook',
  summary:
    'This playbook is suggested because the shop appears to have lower load and utilization than expected. Follow these steps to understand the situation.',
  steps: [
    {
      step_id: 'underutil-1',
      title: 'Review low-load indicators and utilization',
      description:
        'Consider checking the current OBLI and utilization percentage to confirm the degree of underutilization.',
      context_hint: 'Low OBLI combined with low utilization suggests genuinely spare capacity.',
      route_type: 'shop',
    },
    {
      step_id: 'underutil-2',
      title: 'Compare against peer shops in scope',
      description:
        'Consider comparing this shop against others in your scope to see if the low utilization is an outlier.',
      context_hint: 'The Intelligence page shows how this shop compares to peers.',
      route_type: 'intelligence',
    },
    {
      step_id: 'underutil-3',
      title: 'Inspect inbound volume or mix differences',
      description:
        'Consider reviewing whether the shop is receiving fewer tickets or a different job mix than expected.',
      context_hint: 'Low inbound volume may point to external factors rather than operational issues.',
      route_type: 'none',
    },
    {
      step_id: 'underutil-4',
      title: 'Open shop context and review current board',
      description:
        'Consider opening the shop to review the current state and available capacity.',
      context_hint: 'The board view shows actual work distribution and available technician time.',
      route_type: 'shop',
    },
  ],
};

// ── Mapping ──────────────────────────────────────────

const PLAYBOOK_MAP: Record<RecommendationType, Playbook> = {
  OVERLOAD_ATTENTION: OVERLOAD_PLAYBOOK,
  WAITER_PRESSURE: WAITER_PRESSURE_PLAYBOOK,
  COMPLETION_WEAKNESS: COMPLETION_WEAKNESS_PLAYBOOK,
  CAPACITY_IMBALANCE: CAPACITY_IMBALANCE_PLAYBOOK,
  UNDERUTILIZATION: UNDERUTILIZATION_PLAYBOOK,
};

/**
 * Get the playbook for a recommendation type.
 * Returns null if no playbook is defined for the type.
 */
export function getPlaybook(rec: {
  type: string;
  shop_id?: string;
  shop_name?: string;
}): Playbook | null {
  return PLAYBOOK_MAP[rec.type as RecommendationType] ?? null;
}
